import { ReactElement, useState } from 'react';

import {
    TFormalOffer,
    TFormalOfferVariationType
} from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOfferInterestOnlyPeriod } from '@helpers/FormalOffers/getFormalOfferInterestOnlyPeriod';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import {
    GetDrawDownRepaymentScheduleCreditFacilityResponse,
    GetFormalOffersResponse
} from '@iwoca/lapi-client/edge';
import { Link, Pill, Switch } from '@iwoca/orion';
import { Description, Radio } from '@iwoca/orion-ui';
import { useQuery } from '@tanstack/react-query';
import { Label } from '@ui';
import { FormattedMessage } from 'react-intl';

import styles from './OfferChoiceButtons.module.css';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { getNonInterestOnlyPaymentAmount } from '../../../../myOffers/MyOffers.helpers';
import { OfferVariationModal } from '../../offerVariationModal/OfferVariationModal';

export const OfferChoiceButton = ({
    offer,
    selectedOffer,
    updateSelectedOffer,
    drawdownAmount,
    toggles,
    pills
}: {
    offer: TFormalOffer;
    selectedOffer: TFormalOffer | null | undefined;
    updateSelectedOffer: (offer: TFormalOffer) => void;
    drawdownAmount: number;
    toggles: {
        offer: TFormalOffer;
        variationType: TFormalOfferVariationType;
    }[];
    pills: {
        offer: TFormalOffer;
        variationType: TFormalOfferVariationType;
    }[];
}) => {
    const stateKey = useStateKey();

    const { data: minRepaymentAmount } = useQuery({
        queryKey: [
            'draw_down_repayment_schedule',
            stateKey,
            drawdownAmount,
            offer.offer_id,
            offer.product_type
        ],
        queryFn: async () => {
            // eslint-disable-next-line iwoca/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/draw_down_repayment_schedule/${stateKey}/${offer.product_type}/${offer.offer_id}/?amount=${drawdownAmount}`
            );

            return response.json();
        },
        select: (data: GetDrawDownRepaymentScheduleCreditFacilityResponse) =>
            //equal repayments does not exist for interest-only offers
            data.data.summary.simplified_schedule?.equal_repayments
                ? data.data.summary.simplified_schedule?.equal_repayments.amount
                : getCountry() === 'DE' // use first non-interest only repayment for DE
                  ? getNonInterestOnlyPaymentAmount(data.data.schedule)
                  : data.data.summary.simplified_schedule?.next_repayment
                        ?.amount
    });

    const getLabel = (
        offer: NonNullable<GetFormalOffersResponse['data']>['formal_offers'][0]
    ): React.ReactNode => {
        if (getIsInterestOnlyOffer(offer)) {
            const isInterestOnlyPlus =
                getFormalOfferInterestOnlyPeriod(offer)! > 3;

            return (
                <div className={styles.radioButtonLabel}>
                    {isInterestOnlyPlus ? (
                        <FormattedMessage
                            id="_.interest_only.radio_buttons.interest_only_plus_option"
                            defaultMessage="{interestOnlyPeriod} months flexible credit limit, then {remainingLoanDuration} monthly repayments"
                            values={{
                                interestOnlyPeriod:
                                    getFormalOfferInterestOnlyPeriod(offer),
                                remainingLoanDuration:
                                    getDurationInMonths(offer.duration!) -
                                    getFormalOfferInterestOnlyPeriod(offer)!
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="_.interest_only.radio_buttons.interest_only_option"
                            defaultMessage="{loanDuration} monthly repayments, including {interestOnlyPeriod} months of only repaying interest"
                            values={{
                                loanDuration: getDurationInMonths(
                                    offer.duration!
                                ),
                                interestOnlyPeriod:
                                    getFormalOfferInterestOnlyPeriod(offer)
                            }}
                        />
                    )}
                    <span className={styles.radioButtonSubtext}>
                        {minRepaymentAmount && (
                            <FormattedMessage
                                id="_.interest_only.radio_buttons.interest_only_repayment_amount"
                                defaultMessage="(min. {amount} in month {regularRepaymentMonth})"
                                values={{
                                    amount: (
                                        <FormattedCurrency
                                            value={minRepaymentAmount}
                                        />
                                    ),
                                    regularRepaymentMonth:
                                        getFormalOfferInterestOnlyPeriod(
                                            offer
                                        )! + 1
                                }}
                            />
                        )}
                    </span>
                </div>
            );
        } else {
            const loanDuration = getDurationInMonths(offer.duration!);

            return (
                <div className={styles.radioButtonLabel}>
                    <FormattedMessage
                        id="_.duration_choice.radio_buttons.monthly_repayments_option"
                        defaultMessage="{months} monthly repayments"
                        values={{
                            months: loanDuration
                        }}
                    />

                    {minRepaymentAmount && (
                        <span className={styles.radioButtonSubtext}>
                            <FormattedMessage
                                id="_.duration_choice.radio_buttons.repayment_amount"
                                defaultMessage="(min. {amount} per month)"
                                values={{
                                    amount: (
                                        <FormattedCurrency
                                            value={minRepaymentAmount}
                                        />
                                    )
                                }}
                            />
                        </span>
                    )}
                </div>
            );
        }
    };

    const noPGRequired =
        offer.personal_guarantee_proportion !== undefined &&
        offer.personal_guarantee_proportion === 0;

    const isSelected =
        !!selectedOffer &&
        [
            offer.offer_id,
            ...toggles.map((toggle) => toggle.offer.offer_id)
        ].includes(selectedOffer.offer_id);

    return (
        <div>
            <Radio.Frame
                data-testid={`offer-choice-button-${offer.offer_id}`}
                className="px-0"
            >
                <label className="flex cursor-pointer flex-col items-start gap-m px-m">
                    <div className="flex flex-row gap-s">
                        <Radio
                            className="cursor-pointer"
                            checked={isSelected}
                            key={offer.offer_id}
                            id={offer.offer_id}
                            name="choice-buttons"
                            onChange={() => {
                                updateSelectedOffer(offer);
                            }}
                            disabled={drawdownAmount > offer.available_cash!}
                        />
                        <span className="w-full">{getLabel(offer)}</span>
                    </div>
                    {noPGRequired && (
                        <Pill className={`${styles.noPgPill}`}>
                            <FormattedMessage
                                id="_.duration_choice.radio_buttons.no_personal_guarantee_required"
                                defaultMessage="No Personal Guarantee required"
                            />
                        </Pill>
                    )}
                    {pills.map(({ offer, variationType }) => (
                        <OfferVariationPill
                            key={offer.offer_id}
                            variedOffer={offer}
                            variationType={variationType}
                        />
                    ))}
                </label>
            </Radio.Frame>
            {isSelected &&
                toggles.map(({ offer: variedOffer, variationType }) => (
                    <OfferChoiceButtonVariationToggle
                        key={variedOffer.offer_id}
                        variationType={variationType}
                        parentOffer={offer}
                        offer={variedOffer}
                        selectedOffer={selectedOffer}
                        updateSelectedOffer={updateSelectedOffer}
                        drawdownAmount={drawdownAmount}
                    />
                ))}
        </div>
    );
};

const OfferVariationPill = ({
    variedOffer,
    variationType
}: {
    variedOffer: TFormalOffer;
    variationType: TFormalOfferVariationType;
}): ReactElement => {
    switch (variationType) {
        case 'IO':
            return (
                <Pill className={`${styles.noPgPill}`}>
                    <FormattedMessage
                        id="_.interest_only.interest_only_available"
                        defaultMessage="{interestOnlyPayments} month lower repayment period available"
                        values={{
                            interestOnlyPayments:
                                variedOffer.interest_only_repayments
                        }}
                    />
                </Pill>
            );
    }
};

function OfferChoiceButtonVariationToggle({
    variationType,
    ...props
}: {
    variationType: TFormalOfferVariationType;
    parentOffer: TFormalOffer;
    offer: TFormalOffer;
    selectedOffer: TFormalOffer | null;
    updateSelectedOffer: (offer: TFormalOffer) => void;
    drawdownAmount: number;
}): ReactElement {
    switch (variationType) {
        case 'IO':
            return <OfferChoiceButtonInterestOnlyVariationToggle {...props} />;
    }
}

function OfferChoiceButtonInterestOnlyVariationToggle({
    parentOffer,
    offer,
    selectedOffer,
    updateSelectedOffer,
    drawdownAmount
}: {
    parentOffer: TFormalOffer;
    offer: TFormalOffer;
    selectedOffer: TFormalOffer | null;
    updateSelectedOffer: (offer: TFormalOffer) => void;
    drawdownAmount: number;
}) {
    const [isOpen, setOpen] = useState(false);
    return (
        <>
            <label className="flex cursor-pointer flex-col gap-s bg-primary-97 p-l">
                <Label className="flex flex-row justify-between">
                    <FormattedMessage
                        id="_.interest_only.toggle.label"
                        defaultMessage="Add {interestOnlyRepayments, plural, one {# month} other {# months}} of lower repayments"
                        values={{
                            interestOnlyRepayments:
                                offer.interest_only_repayments
                        }}
                    />
                    <Switch
                        checked={selectedOffer?.offer_id === offer.offer_id}
                        onClick={() => {
                            updateSelectedOffer(
                                selectedOffer?.offer_id === offer.offer_id
                                    ? parentOffer
                                    : offer
                            );
                        }}
                    />
                </Label>
                <Description>
                    <FormattedMessage
                        id="_.interest_only.toggle.description"
                        defaultMessage="This will be added at the start, extending the full term to {duration, plural, one {# month} other {# months}}, with interest-only payments during this period."
                        values={{
                            duration: getDurationInMonths(offer.duration!)
                        }}
                    />{' '}
                    <Link
                        className="text-s underline"
                        onClick={(e) => {
                            setOpen(true);
                            // Stop it from selecting the offer
                            e.preventDefault();
                        }}
                    >
                        <FormattedMessage
                            id="_.interest_only.toggle.how_it_works"
                            defaultMessage="How it works"
                        />
                    </Link>
                </Description>
            </label>
            <OfferVariationModal
                isOpen={isOpen}
                onClose={() => setOpen(false)}
                drawdownAmount={drawdownAmount}
                baseOffer={parentOffer}
                variedOffer={offer}
                variationType="IO"
            />
        </>
    );
}
